import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Lock, LockOpen, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { API } from './workbenchShared';

// Worker-days already pushed to QuickBooks are locked against edits (Sep 2026). Unlock one to correct it.
export const PushedDayLocks = ({ token, startDate, endDate, toast }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState(null);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(null);
  const load = () => axios.get(`${API}/qb/pushed-days`, { params: { start_date: startDate, end_date: endDate }, ...auth }).then((r) => setData(r.data)).catch(() => setData({ rows: [], count: 0, auto_lock: true }));
  useEffect(() => { load(); }, [token, startDate, endDate]);
  const unlock = async (row) => {
    if (!window.confirm(`Unlock ${row.worker_name} — ${row.day}?\n\nAfter fixing the hours you must delete that day's time activities in QuickBooks; the next push will resend it.`)) return;
    setBusy(`${row.user_id}-${row.day}`);
    try {
      const r = await axios.post(`${API}/qb/pushed-days/unlock`, { user_id: row.user_id, day: row.day }, auth);
      toast?.({ title: 'Day unlocked', description: r.data.message });
      await load();
    } catch (e) {
      toast?.({ title: 'Could not unlock', description: e.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };
  if (!data) return null;
  return (
    <div className="space-y-1" data-testid="pw-locked-days">
      <button type="button" onClick={() => setShow(!show)} className="text-xs font-semibold text-slate-300 flex items-center gap-1 hover:text-white" data-testid="pw-locked-days-toggle">
        <Lock className="w-3.5 h-3.5" /> Locked days — {data.count} worker-day{data.count === 1 ? '' : 's'} pushed in this period {data.auto_lock ? '' : '(auto-lock is OFF in Settings)'} {show ? '(hide)' : '(show)'}
      </button>
      <p className="text-[11px] text-slate-500">Hours that reached QuickBooks cannot be edited or deleted. Unlock a day only to correct it, then delete its time activities in QuickBooks and push again.</p>
      {show && data.rows.length > 0 && (
        <div className="max-h-40 overflow-auto rounded border border-[#2D3748] text-[11px]">
          {data.rows.map((r) => (
            <div key={`${r.user_id}-${r.day}`} className="flex items-center gap-2 px-2 py-1 border-b border-[#2D3748]/60" data-testid={`pw-locked-day-${r.user_id}-${r.day}`}>
              <span className="text-white">{r.worker_name}</span>
              <span className="text-slate-400">{r.day}</span>
              <span className="text-slate-500">{r.reg_hours != null ? `Reg ${r.reg_hours}h · OT ${r.ot_hours}h` : ''}</span>
              <Button size="sm" variant="ghost" className="ml-auto h-6 px-2 text-[11px]" disabled={busy === `${r.user_id}-${r.day}`} onClick={() => unlock(r)} data-testid={`pw-unlock-day-${r.user_id}-${r.day}`}>
                {busy === `${r.user_id}-${r.day}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <LockOpen className="w-3 h-3 mr-1" />} Unlock
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Green "verified in QuickBooks" badge shown after a push / on push-history rows.
export const VerificationBadge = ({ v, compact = false }) => {
  if (!v) return null;
  if (v.ok === null) return <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-500/20 text-slate-300" title={v.error} data-testid="pw-verify-badge-unknown">Not verified</span>;
  const ok = v.ok;
  const color = ok ? '#00C896' : '#F59E0B';
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ color, background: `${color}22` }} data-testid={ok ? 'pw-verify-badge-ok' : 'pw-verify-badge-problems'}
      title={ok ? `QuickBooks holds ${v.qbo_hours}h for this period — ${v.verified_days}/${v.pushed_days} pushed days match` : `${v.problems} worker-day(s) differ — open Compare with QuickBooks`}>
      {ok ? (compact ? 'Verified' : `Verified in QuickBooks · ${v.verified_days}/${v.pushed_days} days match`) : (compact ? `${v.problems} to check` : `QuickBooks differs on ${v.problems} worker-day${v.problems === 1 ? '' : 's'}`)}
    </span>
  );
};
